import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  DialogActions,
  DialogContent,
  makeStyles,
} from "@material-ui/core";
import {
  CorpModalScreens,
  IPerson,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
} from "redmond";
import {
  personToTravelerProfile,
  travelerProfileToPerson,
  TravelerProfile,
  TripsTravelerInfoForm,
  CorporateButton,
} from "halifax";
import { ArrowBack } from "@material-ui/icons";
import clsx from "clsx";
import { CorporateEmbeddedProvider, EmbeddedTravelerForm } from "@capone-corporate/portal";
import { useExperimentIsVariant } from "@capone/experiments";
import { StepProps } from "../types";
import Header from "../Header";

import baseClasses from "../../styles.module.css";
import { trackEvent } from "../../../../../api/v1/trackEvent";
import { config } from "../../../../../api/config";

interface IProps extends Omit<StepProps, "primaryAction"> {
  primaryAction: (traveler: IPerson) => Promise<void>;
  traveler: IPerson;
  isMobile?: boolean;
}

const useStyles = makeStyles(() => ({
  leftButtonWrapper: {
    "& .MuiButton-label": {
      justifyContent: "normal",
    },
  },
  mobileButton: {
    "& .MuiButton-label": {
      justifyContent: "center",
    },
  },
  mobileButtonContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  },
}));

const AdditionalDetails = ({
  primaryAction,
  secondaryAction,
  onClose,
  traveler,
  isMobile = false,
}: IProps): React.ReactElement => {
  const useEmbeddedTravelerForm = useExperimentIsVariant(
    "corp-embedded-traveler-form",
    "available"
  );

  const [hasSaved, setHasSaved] = useState(false);
  const [isFormEmpty, setIsFormEmpty] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const classes = useStyles();

  const modalTitle = "Save additional details";
  const modalSubtitle = "Add your travel details now, or skip to add them later when you book.";

  const embeddedTraveler = useMemo(
    () => personToTravelerProfile(traveler),
    [traveler]
  );

  const handleSecondaryClick = useCallback(() => {
    secondaryAction(null, 1);
  }, [secondaryAction]);

  const commonTrackingProperties = useMemo(
    () =>
    ({
      type: "save_additional_traveler_profile_details_onboarding",
      screen: CorpModalScreens.ONBOARDING_ADDITIONAL,
      primary_button: "save and continue",
      secondary_button: "back",
      step: CorpModalScreens.ONBOARDING_ADDITIONAL,
      funnel: "other",
      category: ModalCategoryType.FEATURE,
      modal_subtitle: modalSubtitle,
      modal_title: modalTitle,
      agent_title: modalTitle,
      agent_subtitle: modalSubtitle,
    } satisfies ModalAlertProperties),
    []
  );

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: commonTrackingProperties,
    });
  }, []);

  // `TripsTravelerInfoForm` only runs its save on a false→true transition of
  // `triggerSave`. If the save is blocked by form validation, `hasSaved` would
  // stay `true`, so a second click (after fixing the errors) wouldn't produce a
  // new transition and nothing would happen. Reset it back to `false` after
  // each trigger so every click re-attempts the save.
  useEffect(() => {
    if (hasSaved) {
      setHasSaved(false);
    }
  }, [hasSaved]);

  const handleSaveClick = () => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: ModalButtonType.PRIMARY,
      },
    });
    setHasSaved(true);
  };

  const back = () => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: ModalButtonType.SECONDARY,
      },
    });
    handleSecondaryClick();
  };

  const handleSkip = () => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: ModalButtonType.SECONDARY,
      },
    });
    onClose();
  };

  const renderEmbeddedForm = () => (
    <CorporateEmbeddedProvider>
      <EmbeddedTravelerForm
        mode="edit"
        traveler={embeddedTraveler}
        showTravelerInfo={false}
        showFlightInfo
        showFrequentFlyer
        showHotelLoyalty
        isHotelLoyaltyOptional
        showCarLoyalty
        showPassport
        showPassportDetails
        showNationality
        showSkip
        cancelButtonText="Back"
        actionsConfig={{ layout: "split", sticky: true }}
        onSave={(profile: TravelerProfile) => {
          const updatedTraveler = travelerProfileToPerson(
            profile,
            traveler
          ) as IPerson;
          trackEvent({
            eventName: MODAL_ALERT_CHOICE,
            properties: {
              ...commonTrackingProperties,
              button_choice: ModalButtonType.PRIMARY,
            },
          });
          return primaryAction(updatedTraveler);
        }}
        onCancel={back}
        onSkip={handleSkip}
      />
    </CorporateEmbeddedProvider>
  );

  const mobileActions = (
    <>
      {!isFormEmpty ? (
        <CorporateButton
          className={classes.mobileButton}
          size="large"
          data-testid="traveler-additional-details-primary-cta"
          onClick={handleSaveClick}
          disabled={isSaveDisabled}
          fullWidth
        >
          Save and continue
        </CorporateButton>
      ) : (
        <CorporateButton
          className={classes.mobileButton}
          size="large"
          onClick={onClose}
          variant="outlined"
          fullWidth
        >
          Skip
        </CorporateButton>
      )}
      <CorporateButton
        className={classes.mobileButton}
        onClick={back}
        size="large"
        variant="text"
        icon={<ArrowBack fontSize="small" />}
        data-testid="traveler-additional-details-secondary-cta"
        fullWidth
      >
        Back
      </CorporateButton>
    </>
  );

  const desktopActions = (
    <>
      <CorporateButton
        className={classes.leftButtonWrapper}
        onClick={back}
        size="medium"
        variant="text"
        icon={<ArrowBack fontSize="small" />}
        data-testid="traveler-additional-details-secondary-cta"
      >
        Back
      </CorporateButton>
      <Box style={{ display: "flex" }}>
        {isFormEmpty && (
          <Box style={{ marginRight: "16px" }}>
            <CorporateButton size="medium" onClick={onClose} variant="outlined">
              Skip
            </CorporateButton>
          </Box>
        )}
        <CorporateButton
          size="medium"
          data-testid="traveler-additional-details-primary-cta"
          onClick={handleSaveClick}
          disabled={isSaveDisabled || isFormEmpty}
        >
          Save and continue
        </CorporateButton>
      </Box>
    </>
  );

  const renderLegacyFormContent = () => (
    <TripsTravelerInfoForm
      traveler={traveler}
      handleUpdatePassenger={primaryAction}
      buttonText="Save and continue"
      showTravelerInfoSection={false}
      showAdditionalInfoSection
      showFrequentFlyerSection
      showHotelLoyaltySection
      showGroundLoyaltySection
      additionalInfoTitle="Additional flight information"
      frequentFlyerTitle="Frequent flyer details"
      hotelLoyaltyTitle="Hotel loyalty details"
      groundLoyaltyTitle="Car rental loyalty details"
      showSecondaryButton
      secondaryButtonText="Back"
      onSecondaryButtonClick={handleSecondaryClick}
      triggerSave={hasSaved}
      setPrimaryButtonDisabled={setIsSaveDisabled}
      hideButtons
      showPassportSection
      className={baseClasses.onboardingModalForm}
      tenant={config.TENANT}
      onAdditionalDetailsDataEmpty={setIsFormEmpty}
      isMobile={isMobile}
    />
  );

  const renderLegacyFormActions = () => (
      <DialogActions
        className={clsx(
          { "mobile-dialog-actions": isMobile },
          "additional-details-actions"
        )}
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: !isMobile
            ? "space-between"
            : "flex-end",
          width: "auto",
          alignItems: "center",
          boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.15)",
          boxSizing: isMobile ? "border-box" : "content-box",
        }}
      >
        {isMobile ? mobileActions : desktopActions}
      </DialogActions>
  );

  return (
    <Box className={baseClasses.stepWrap}>
      <Header
        title={modalTitle}
        subtitle="Add your travel details now, or skip to add them later when you book."
        currentStep={1}
        onClose={onClose}
        isMobile={isMobile}
      />
      <DialogContent
        id="onboarding-description"
        style={{
          flex: "1 1 auto",
          overflow: useEmbeddedTravelerForm ? "hidden" : "auto",
          minHeight: 0,
          display: useEmbeddedTravelerForm ? "flex" : undefined,
          flexDirection: useEmbeddedTravelerForm ? "column" : undefined,
          isolation: "isolate",
          contain: "paint",
          ...(isMobile ? { paddingLeft: 0, paddingRight: 0 } : {}),
        }}
      >
        <Box
          style={{
            ...(useEmbeddedTravelerForm
              ? {
                  height: "100%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }
              : {}),
            ...(isMobile ? { paddingLeft: 16, paddingRight: 16 } : {}),
          }}
        >
          {useEmbeddedTravelerForm
            ? renderEmbeddedForm()
            : renderLegacyFormContent()}
        </Box>
      </DialogContent>
      {!useEmbeddedTravelerForm && renderLegacyFormActions()}
    </Box>
  );
};

export default AdditionalDetails;
